import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import CenteredInfoLayout from '~/components/layout/templates/CenteredInfo';
import Button from '~/components/new/Button';
import Alert from '~/components/ui/Alert';
import Header from '~/components/ui/Header';
import Icon, { RemixIconNames } from '~/components/ui/Icon';
import Loading from '~/components/ui/Loading';
import type { SignResponse } from '~/types/platform';
import { isLocal } from '~/utils/env';
import { useQuery } from '~/utils/router';
import { sign, signInfo } from './api';

export const docNames: Record<string, string> = {
    CA: 'Consulting Agreement',
    tax_form: 'Tax Form',
    code_of_conduct: 'Code of Conduct',
    EA: 'Equipment Agreement',
    termination: 'Termination Agreement',
    amendment: 'Amendment',
};

export function getDocName(type: string | undefined) {
    return (type && docNames[type]) || 'document';
}

export default function SignAgreementRoute() {
    const { type, id: rawId } = useParams();
    const id = Number(rawId);
    const navigate = useNavigate();
    const query = useQuery();
    const returnTo = query.get('return_to');

    const [alreadySigned, setAlreadySigned] = useState(false);
    const [signed, setSigned] = useState(false);

    const backAction = {
        text: returnTo ? 'Continue' : 'Go back home',
        url: returnTo || '/',
    };

    if (alreadySigned) {
        return (
            <CenteredInfoLayout verticallyAligned>
                <CenteredInfoLayout.Header
                    icon='info'
                    title={`This ${getDocName(type)} is already signed`}
                />
                <Button
                    className='mx-auto'
                    onClick={() => {
                        void navigate(backAction.url);
                    }}>
                    {backAction.text}
                </Button>
            </CenteredInfoLayout>
        );
    } else if (signed) {
        return (
            <CenteredInfoLayout verticallyAligned>
                <CenteredInfoLayout.Header
                    icon='confirm'
                    title={`Thanks for signing this ${getDocName(type)}`}
                />
                <Button
                    className='mx-auto'
                    onClick={() => {
                        void navigate(backAction.url);
                    }}>
                    {backAction.text}
                </Button>
            </CenteredInfoLayout>
        );
    }

    return (
        <Sign
            type={type}
            id={id}
            getSignInfo={() => signInfo(id)}
            onAlreadySigned={() => setAlreadySigned(true)}
            setSigned={() => sign(id)}
            onSigned={() => setSigned(true)}
        />
    );
}

interface SignProps {
    type?: string;
    id: number;
    getSignInfo: () => Promise<SignResponse>;
    onAlreadySigned: () => void;
    setSigned: () => Promise<SignResponse>;
    onSigned: () => void;
}

export function Sign({ type, id, getSignInfo, onAlreadySigned, setSigned, onSigned }: SignProps) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [signError, setSignError] = useState(false);
    const [url, setUrl] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(1);

    useEffect(() => {
        setError(false);
        getSignInfo()
            .then((data) => {
                if (!data.url) {
                    window.setTimeout(() => {
                        setAttempt(attempt + 1);
                    }, 1000);
                } else if (!data.can_sign) {
                    onAlreadySigned();
                } else {
                    setLoading(false);
                    setUrl(data.url);
                }
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });
    }, [id, type, attempt]);

    const handleComplete = () => {
        setSigned()
            .then(() => {
                onSigned();
            })
            .catch(() => {
                setSignError(true);
            });
    };

    return (
        <div
            className='offers-sign'
            data-test-component='agreements/Sign'>
            <Header className='flex-column gap-2'>
                <Header.Title>Sign {getDocName(type)}</Header.Title>
                <div className='flex flex-column gap-4 items-center'>
                    <span className='desktop-body-1-regular'>
                        Review and sign the agreement below.
                    </span>
                    <div className='desktop-caption-1-regular text-core-secondary flex gap-1'>
                        <Icon icon={RemixIconNames.RiShieldCheckLine} />
                        Secure signing powered by PandaDoc
                    </div>
                </div>
            </Header>

            {loading && (
                <div className='offers-sign-loading'>
                    <p>Generating the document to sign. This may take a moment.</p>
                    <p>
                        In case this takes too long, you'll receive an email from Pandadoc once the
                        agreement is ready to be signed.
                    </p>
                    <Loading />
                </div>
            )}

            {!loading && error && (
                <Alert variant='danger'>Error creating the document to sign.</Alert>
            )}

            {signError && <Alert variant='danger'>Error signing document.</Alert>}

            {!loading && !error && url && (
                <div className='offers-sign-doc'>
                    {isLocal() && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                paddingBottom: '30px',
                            }}>
                            <Button
                                onClick={handleComplete}
                                icon={RemixIconNames.RiEdit2Fill}
                                data-test-name='emulateSignature'>
                                Emulate signature
                            </Button>
                        </div>
                    )}
                    <SignPandadocForm
                        url={url}
                        onComplete={handleComplete}
                    />
                </div>
            )}
        </div>
    );
}

function SignPandadocForm({ url, onComplete }: { url: string; onComplete: () => void }) {
    const ref = useRef<HTMLDivElement>(null);
    const iframeRendered = useRef(false);

    useEffect(
        function () {
            if (!ref.current || iframeRendered.current) return;

            const iframe = document.createElement('iframe');
            iframe.style.border = 'none';
            iframe.src = url;
            // no 100% or other relative units because it breaks "Start signing" Pandadoc button
            iframe.height = document.body.clientHeight.toString();
            iframe.width = '100%';

            const node = ref.current;
            node.style.height = '100%';
            node.style.width = '100%';

            node.append(iframe);
            iframeRendered.current = true;

            window.addEventListener(
                'message',
                function (e) {
                    if (
                        e &&
                        e.data &&
                        iframe &&
                        e.source === iframe.contentWindow &&
                        typeof e.data === 'string'
                    ) {
                        try {
                            const message = JSON.parse(e.data) as {
                                event?: string;
                                data?: unknown;
                            };
                            if (message && message.event) {
                                const event = message.event.replace('embed.form.', '');
                                if (event === 'completed') {
                                    onComplete();
                                }
                            }
                        } catch (err) {
                            console.error(err);
                        }
                    }
                },
                false,
            );
        },
        [ref],
    );

    return <div ref={ref}></div>;
}
